import { createColumnHelper, flexRender, Header, useTable } from "@tanstack/react-table"
import { Tooltip, TooltipContent, TooltipTrigger } from "design-system/atoms/Tooltip"
import DateDisplay from "design-system/molecules/DateDisplay"
import {
  Table,
  TableBody,
  TableCell,
  TableEmptySlate,
  TableHead,
  TableHeader,
  TableRow,
  TableSkeleton,
} from "design-system/molecules/Table"
import { features, type Features } from "lib/table"
import { CompanyClientCell } from "modules/customers/components/CompanyClientCell"
import { useIsAmphoraCompany } from "modules/customers/hooks/useIsAmphoraCompany"
import { CustomerCell } from "modules/shared/components/CustomerCell"
import { ActionRequestStatusIcon } from "modules/support/components/entities/ActionRequestEntityRow"
import { useActionRequestsQuery } from "modules/support/hooks/useActionRequestsQuery"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { SearchSchema } from "routes/_base/_auth/support/list/route"
import { SupportActionRequestsResponseDataSchema, SupportSortFields } from "schema"
import { z } from "zod"

const columnHelper = createColumnHelper<
  Features,
  z.infer<typeof SupportActionRequestsResponseDataSchema>[number]
>()

type ActionRequestsQueryReturn = ReturnType<typeof useActionRequestsQuery>

type Props = {
  actionRequests: ActionRequestsQueryReturn["data"]
  isPending: ActionRequestsQueryReturn["isPending"]
  fields: typeof SupportSortFields
  onSort: (field: (typeof SupportSortFields)[number]) => void
  search: z.infer<typeof SearchSchema>
}

export function ActionRequestsTable({ actionRequests, isPending, fields, onSort, search }: Props) {
  const { t } = useTranslation()
  const isAmphoraCompany = useIsAmphoraCompany()

  const columns = useMemo(
    () =>
      columnHelper.columns([
        columnHelper.display({
          id: "status",
          header: () => null,
          cell: ({ row }) => <ActionRequestStatusIcon status={row.original.status} />,
          meta: {
            className: "w-px pr-3",
          },
        }),
        columnHelper.accessor("category", {
          header: t("support.actionRequests.table.category"),
          cell: (props) => {
            const category = props.getValue()
            if (!category) return <span className="text-muted-foreground">{"-"}</span>
            return <span className="font-medium">{category}</span>
          },
          meta: {
            colSpan: 1,
            className: "min-w-24 max-w-0 truncate",
          },
        }),
        columnHelper.accessor("summary", {
          header: t("support.actionRequests.table.summary"),
          cell: (props) => (
            <Tooltip>
              <TooltipTrigger className="block max-w-full truncate">
                {props.getValue()}
              </TooltipTrigger>
              <TooltipContent className="max-w-80">{props.getValue()}</TooltipContent>
            </Tooltip>
          ),
          meta: {
            colSpan: 4,
            className: "w-full min-w-48 max-w-0",
          },
        }),
        ...(!isAmphoraCompany
          ? [
              columnHelper.accessor("customer", {
                header: () => t("support.actionRequests.table.customer"),
                cell: ({ row }) => <CustomerCell customer={row.original.customer} />,
                meta: {
                  colSpan: 2,
                  className: "min-w-32 max-w-0 truncate",
                },
              }),
            ]
          : []),
        ...(isAmphoraCompany
          ? [
              columnHelper.display({
                id: "client",
                header: t("support.actionRequests.table.client"),
                cell: ({ row }) => <CompanyClientCell client={row.original.customer.client} />,
                meta: {
                  className: "min-w-24 max-w-0 truncate",
                },
              }),
              columnHelper.display({
                id: "orderId",
                header: t("support.actionRequests.table.orderId"),
                cell: ({ row }) => row.original.conversation.orderId ?? "-",
                meta: {
                  className: "min-w-24 max-w-0 truncate",
                },
              }),
            ]
          : []),
        columnHelper.accessor("campaign", {
          header: t("support.actionRequests.table.campaign"),
          cell: (props) => props.getValue()?.name ?? "-",
          meta: {
            colSpan: 2,
            className: "min-w-24 max-w-0 truncate",
          },
        }),
        columnHelper.accessor("createdAt", {
          header: t("support.actionRequests.table.createdAt"),
          cell: (props) => <DateDisplay date={props.getValue()} />,
          meta: {
            colSpan: 1,
            align: "right",
            className: "w-px",
          },
        }),
      ]),
    [isAmphoraCompany, t],
  )

  const table = useTable({
    data: actionRequests ?? [],
    columns,
    features,
  })

  function renderContent() {
    if (isPending || actionRequests === undefined) {
      return <TableSkeleton colSpans={columns.map((c) => c.meta?.colSpan ?? 1)} />
    }

    if (actionRequests.length === 0) {
      return (
        <TableEmptySlate
          colSpan={columns.reduce((acc, c) => acc + (c.meta?.colSpan ?? 1), 0)}
          message={t("support.actionRequests.table.empty")}
        />
      )
    }

    return table.getRowModel().rows.map((row) => (
      <TableRow
        key={row.original.id}
        navigate={{
          to: "/support/list/$customerId",
          params: { customerId: row.original.customer.id },
          search: {
            ...search,
            conversationId: Number(row.original.conversationId),
            tab: "conversations",
            supportTab: "actionRequests",
          },
        }}
      >
        {row.getVisibleCells().map((cell) => (
          <TableCell key={cell.id} {...cell.column.columnDef.meta}>
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </TableCell>
        ))}
      </TableRow>
    ))
  }

  function buildSortable(
    header: Header<
      Features,
      z.infer<typeof SupportActionRequestsResponseDataSchema>[number],
      unknown
    >,
  ) {
    if (!fields.includes(header.id as (typeof SupportSortFields)[number])) return

    const isActive = search.field === header.id

    return {
      active: isActive,
      direction: isActive ? search.order : "asc",
      onClick: () => onSort(header.id as (typeof SupportSortFields)[number]),
    }
  }

  return (
    <Table>
      <TableHeader>
        {table.getHeaderGroups().map((headerGroup) => (
          <TableRow key={headerGroup.id}>
            {headerGroup.headers.map((header) => (
              <TableHead
                key={header.id}
                sortable={buildSortable(header)}
                {...header.column.columnDef.meta}
              >
                {flexRender(header.column.columnDef.header, header.getContext())}
              </TableHead>
            ))}
          </TableRow>
        ))}
      </TableHeader>
      <TableBody>{renderContent()}</TableBody>
    </Table>
  )
}
